import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { GaugeBar } from '../../components/charts/GaugeBar';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { LoadingIndicator } from '../../components/Spinner';
import { Tabs } from '../../components/Tabs';
import { formatDateTime } from '../../i18n';
import { useSession } from '../auth/queries';
import { CustomerKnowledgePanel } from '../knowledge/CustomerKnowledgePanel';
import { toneForPriorityLevel, toneForStateCategory } from '../tickets/presentation';
import { ActivityPanel } from './ActivityPanel';
import { ContactPanel } from './ContactPanel';
import { ContractPanel } from './ContractPanel';
import { CustomerDialog } from './CustomerDialog';
import { SitePanel } from './SitePanel';
import { useCustomerFile, useDeleteCustomer } from './queries';

interface CustomerFilePageProps {
  customerId: string;
}

/**
 * Die Kundenakte -- F2-04.
 *
 * Alles an einer Stelle, was ein Bearbeiter vor einem Anruf wissen muss. Der
 * Server liefert es in **einer** Antwort; die Reiter teilen es auf, ohne
 * nachzuladen.
 *
 * Oben stehen die Zahlen, die man im Blick behaelt: offene Tickets,
 * geschlossene, laufende Vertraege. Darunter die Bestaende, die man
 * bearbeitet.
 *
 * **Die Bahn unter den Zahlen setzt sie ins Verhaeltnis.** "Drei offen" ist
 * bei einem Kunden mit vier Vorgaengen im Jahr etwas anderes als bei einem
 * mit vierhundert -- und genau diesen Unterschied sieht man an zwei
 * nebeneinanderstehenden Zahlen nicht.
 */
export function CustomerFilePage({ customerId }: CustomerFilePageProps) {
  const { t, i18n } = useTranslation();
  const query = useCustomerFile(customerId);
  const session = useSession();
  const remove = useDeleteCustomer();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const canWrite = (session.data?.permissions ?? []).includes('crm.write');

  if (query.isPending) return <LoadingIndicator />;
  if (query.isError) return <ErrorNotice error={query.error} />;

  const file = query.data;
  const customer = file.customer;

  // Die Listen tragen im Schema eine Vorgabe und sind deshalb wahlfrei. Sie
  // hier einmal auszupacken ist kuerzer und ehrlicher, als an fuenfzehn
  // Stellen ein Fragezeichen zu setzen.
  const sites = file.sites ?? [];
  const contacts = file.contacts ?? [];
  const contracts = file.contracts ?? [];
  const children = file.children ?? [];
  const recentTickets = file.recent_tickets ?? [];
  const timeline = file.timeline ?? [];

  const activeContracts = contracts.filter((contract) => contract.status === 'active');

  return (
    <>
      <PageHeader
        title={
          <>
            {customer.name}
            {customer.is_vip ? (
              <>
                {' '}
                <Badge tone="warning" dot>
                  {t('crm.vip')}
                </Badge>
              </>
            ) : null}
            {/* Nur der abgeschaltete Fall bekommt ein Schild. Der Regelfall
                      braucht keines -- ein Hinweis an jedem Kunden waere keiner. */}
            {!customer.accepts_email_notifications ? (
              <>
                {' '}
                <Badge tone="neutral">{t('crm.emailNotificationsOff')}</Badge>
              </>
            ) : null}
          </>
        }
        subtitle={customer.customer_number}
        action={
          canWrite ? (
            <div className="row">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(true);
                }}
              >
                {t('crm.editCustomer')}
              </Button>
              {/* Der Regelfall ist "inaktiv" und nicht "geloescht" -- die
                        Tickets eines Kunden sind der Nachweis darueber, was fuer ihn
                        getan wurde. Geloescht wird ein Kunde, den jemand versehentlich
                        angelegt hat; der Server weist einen mit Tickets ab. */}
              <Button
                variant="ghost"
                busy={remove.isPending}
                onClick={() => {
                  if (!window.confirm(t('crm.deleteCustomerConfirm'))) return;
                  remove.mutate(customerId, {
                    onSuccess: () => {
                      void navigate({ to: '/app/crm' });
                    },
                  });
                }}
              >
                {t('crm.deleteCustomer')}
              </Button>
            </div>
          ) : null
        }
      />

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <div className="stat-row">
        <div className="stat">
          <span className="stat__value">{file.open_tickets}</span>
          <span className="stat__label">{t('crm.openTickets')}</span>
        </div>
        <div className="stat">
          <span className="stat__value">{file.closed_tickets}</span>
          <span className="stat__label">{t('crm.closedTickets')}</span>
        </div>
        <div className="stat">
          <span className="stat__value">{activeContracts.length}</span>
          <span className="stat__label">{t('crm.activeContracts')}</span>
        </div>
        <div className="stat">
          <span className="stat__value">{contacts.length}</span>
          <span className="stat__label">{t('crm.contacts')}</span>
        </div>
      </div>

      {file.open_tickets + file.closed_tickets > 0 ? (
        <section className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <GaugeBar
            label={t('crm.openShare')}
            percent={(file.open_tickets / (file.open_tickets + file.closed_tickets)) * 100}
            display={t('crm.openOfTotal', {
              open: file.open_tickets,
              total: file.open_tickets + file.closed_tickets,
            })}
            tone={file.open_tickets > 0 ? 'warning' : 'success'}
          />
        </section>
      ) : null}

      <div className="detail-grid">
        <div className="stack">
          <Tabs
            ariaLabel={t('crm.sections')}
            tabs={[
              {
                value: 'overview',
                label: t('crm.overview'),
                content: (
                  <div className="stack">
                    <section className="card">
                      <h2>{t('crm.recentTickets')}</h2>
                      {recentTickets.length === 0 ? (
                        <p className="table__muted">{t('crm.noTickets')}</p>
                      ) : (
                        <ul className="task-list">
                          {recentTickets.map((ticket) => (
                            <li key={ticket.id} className="task-list__item">
                              <Link
                                to="/app/tickets/$ticketId"
                                params={{ ticketId: ticket.id }}
                                className="table__number"
                              >
                                {ticket.ticket_number}
                              </Link>
                              <span>{ticket.title}</span>
                              <Badge tone={toneForStateCategory(ticket.state.state_category)} dot>
                                {ticket.state.name}
                              </Badge>
                              <Badge tone={toneForPriorityLevel(ticket.priority.level)}>
                                {ticket.priority.name}
                              </Badge>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    <section className="card">
                      <h2>{t('crm.timeline')}</h2>
                      <p className="table__muted">{t('crm.timelineHint')}</p>
                      {timeline.length === 0 ? (
                        <p className="table__muted">{t('crm.noTimeline')}</p>
                      ) : (
                        <ol className="timeline">
                          {timeline.map((entry) => (
                            <li key={`${entry.source}-${entry.id}`} className="timeline__entry">
                              <div className="timeline__head">
                                <Badge tone={entry.source === 'activity' ? 'info' : 'neutral'}>
                                  {t(`channel.${entry.channel}`, { defaultValue: entry.channel })}
                                </Badge>
                                {entry.ticket_number ? (
                                  <Link
                                    to="/app/tickets/$ticketId"
                                    params={{ ticketId: entry.ticket_id ?? '' }}
                                    className="table__number"
                                  >
                                    {entry.ticket_number}
                                  </Link>
                                ) : null}
                                <span className="timeline__time">
                                  {formatDateTime(entry.occurred_at, i18n.language)}
                                </span>
                              </div>
                              {entry.subject ? (
                                <p className="timeline__subject">{entry.subject}</p>
                              ) : null}
                              <p className="prose">{entry.excerpt}</p>
                            </li>
                          ))}
                        </ol>
                      )}
                    </section>
                  </div>
                ),
              },
              {
                value: 'contacts',
                label: t('crm.contacts'),
                count: contacts.length,
                content: (
                  <ContactPanel
                    customerId={customerId}
                    contacts={contacts}
                    sites={sites}
                    canWrite={canWrite}
                  />
                ),
              },
              {
                value: 'sites',
                label: t('crm.sites'),
                count: sites.length,
                content: <SitePanel customerId={customerId} sites={sites} canWrite={canWrite} />,
              },
              {
                value: 'contracts',
                label: t('crm.contracts'),
                count: contracts.length,
                content: (
                  <ContractPanel
                    customerId={customerId}
                    contracts={contracts}
                    canWrite={canWrite}
                  />
                ),
              },
              {
                value: 'activities',
                label: t('crm.activities'),
                content: (
                  <ActivityPanel customerId={customerId} contacts={contacts} canWrite={canWrite} />
                ),
              },
              /* Wissensmanagement -- F4-25. Der Reiter beantwortet die Frage,
                 die beim Kunden gestellt wird: "Was wissen wir ueber dieses
                 Haus?" Der umgekehrte Weg -- vom Artikel zum Kunden -- gibt es
                 in der Wissensdatenbank schon. */
              {
                value: 'knowledge',
                label: t('crm.knowledge'),
                content: <CustomerKnowledgePanel customerId={customerId} />,
              },
            ]}
          />
        </div>

        <aside className="card">
          <dl className="definition-list">
            <dt>{t('crm.status')}</dt>
            <dd>
              <Badge tone={customer.status === 'active' ? 'success' : 'neutral'}>
                {t(`customerStatus.${customer.status}`)}
              </Badge>
            </dd>

            <dt>{t('crm.industry')}</dt>
            <dd>{customer.industry ?? <span className="table__muted">{t('app.none')}</span>}</dd>

            <dt>{t('crm.vatId')}</dt>
            <dd>{customer.vat_id ?? <span className="table__muted">{t('app.none')}</span>}</dd>

            <dt>{t('crm.website')}</dt>
            <dd>{customer.website ?? <span className="table__muted">{t('app.none')}</span>}</dd>

            {/* Die Domaenen stehen in der Akte und nicht nur im Dialog: Wer
                einem falsch zugeordneten Vorgang nachgeht, sieht hier den
                Grund, ohne die Maske zum Aendern zu oeffnen. */}
            <dt>{t('crm.mailDomains')}</dt>
            <dd>
              {customer.mail_domains.length === 0 ? (
                <span className="table__muted">{t('app.none')}</span>
              ) : (
                customer.mail_domains.join(', ')
              )}
            </dd>

            <dt>{t('tickets.createdAt')}</dt>
            <dd>{formatDateTime(customer.created_at, i18n.language)}</dd>
          </dl>

          {file.parent || children.length > 0 ? (
            <>
              <h2 style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>{t('crm.group')}</h2>
              {file.parent ? (
                <p>
                  {t('crm.parent')}:{' '}
                  <Link to="/app/crm/$customerId" params={{ customerId: file.parent.id }}>
                    {file.parent.name}
                  </Link>
                </p>
              ) : null}
              {children.length > 0 ? (
                <ul className="task-list">
                  {children.map((child) => (
                    <li key={child.id} className="task-list__item">
                      <Link to="/app/crm/$customerId" params={{ customerId: child.id }}>
                        {child.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}

          {customer.notes ? (
            <>
              <h2 style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>{t('crm.notes')}</h2>
              <p className="prose">{customer.notes}</p>
            </>
          ) : null}
        </aside>
      </div>

      {editing ? (
        <CustomerDialog open={editing} onOpenChange={setEditing} customer={customer} />
      ) : null}
    </>
  );
}
